import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle, XCircle, Clock, PencilSimple, Sparkle, ChatCircleDots, PaperPlaneTilt, WarningCircle, Copy, Trash, EnvelopeSimple, Link as LinkIcon, ArrowSquareOut, CalendarPlus, Prohibit, Eye, MagnifyingGlass, DownloadSimple, FileArrowDown, FilePdf, FileImage } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useApi } from "@/api/useApi";
import { useRole } from "@/hooks/useRole";
import { useUser } from "@clerk/clerk-react";
import CommentsThread from "@/components/CommentsThread";
import { hasUnread } from "@/lib/commentReads";
import ImageEditor from "@/components/ImageEditor";
import { PlatformMockup } from "@/components/PlatformMockup";
import { FullPreviewModal } from "@/components/FullPreview/FullPreviewModal";
import { stripMd } from "@/lib/richText";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const platformColors = {
  facebook: "#1877F2", instagram: "#E1306C", linkedin: "#0A66C2", tiktok: "#010101",
};

const NATIVE_PROVIDER_FOR_PLATFORM = { linkedin: "linkedin", tiktok: "tiktok" };

const copyPlain = async (text) => {
  try { await navigator.clipboard.writeText(stripMd(text)); toast.success("Copied — plain text, no highlight"); }
  catch { toast.error("Couldn't copy — try selecting the text manually"); }
};

const matchesSearch = (query, fields) => {
  const q = (query || "").trim().toLowerCase();
  if (!q) return true;
  return fields.some((f) => (f || "").toString().toLowerCase().includes(q));
};

export default function ApprovalQueue() {
  const api = useApi();
  const { canApprove, canManageContent, canDelete } = useRole();
  const [searchParams] = useSearchParams();
  const [view, setView] = useState(searchParams.get("view") === "newsletters" ? "newsletters" : "review"); // review | ready | newsletters
  const [posts, setPosts] = useState([]);
  const [approved, setApproved] = useState([]);
  const [newsletters, setNewsletters] = useState([]);
  const [newsletterFeedback, setNewsletterFeedback] = useState({});
  const [newsletterBusy, setNewsletterBusy] = useState(null);
  const [deleteNewsletterTarget, setDeleteNewsletterTarget] = useState(null);
  const [sendMailchimpTarget, setSendMailchimpTarget] = useState(null);
  const [scheduleTarget, setScheduleTarget] = useState(null);
  const [scheduleTime, setScheduleTime] = useState({});
  const [unscheduleTarget, setUnscheduleTarget] = useState(null);
  const [media, setMedia] = useState({});
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState({});
  const [editing, setEditing] = useState({}); // postId -> edited text
  const [busy, setBusy] = useState(null);
  const [filterMode, setFilterMode] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [openComments, setOpenComments] = useState({});
  const [editMedia, setEditMedia] = useState(null); // {postId, media}
  const [deleteTarget, setDeleteTarget] = useState(null); // post id awaiting delete confirm
  const [fbDirect, setFbDirect] = useState(null); // {connected, instagram_linked}
  const [native, setNative] = useState(null); // {linkedin:{...}, tiktok:{...}, ...}
  const [brand, setBrand] = useState({ name: "Your Brand", logoUrl: null });
  const [batchBusy, setBatchBusy] = useState(false);
  const [approveAllTarget, setApproveAllTarget] = useState(false);
  const [fullPreview, setFullPreview] = useState(null);
  const { user } = useUser();

  const openPostFullPreview = (post, caption) => setFullPreview({
    type: "social",
    data: {
      platform: post.platform, mediaUrl: post.attached_media_url, mediaKind: post.attached_media_kind,
      mediaId: post.attached_media_id, caption, brandName: brand.name, brandLogoUrl: brand.logoUrl,
    },
  });
  const openNewsletterFullPreview = (n) => setFullPreview({ type: "newsletter", data: { html: n.rendered_html, subject: n.subject } });

  const loadBrand = useCallback(async () => {
    try {
      const b = await api.get("/brand");
      const name = (b.data?.logo_text || "").trim();
      setBrand({ name: name && name.toLowerCase() !== "your brand" ? name : "Your Brand", logoUrl: b.data?.logo_url || null });
    } catch { /* Approvers can't read /brand — fall back to the generic placeholder */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSocial = useCallback(async () => {
    if (!canManageContent) return;
    try {
      const r = await api.get("/facebook-direct/status");
      setFbDirect(r.data || { connected: false });
    } catch (_) { setFbDirect({ connected: false }); }
    try {
      const r2 = await api.get("/social/native/status");
      setNative(r2.data || {});
    } catch (_) { setNative({}); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageContent]);

  const load = useCallback(async () => {
    try {
      const [p, a] = await Promise.all([
        api.get(`/posts?status=pending_review`),
        api.get(`/posts?status=approved`),
      ]);
      setPosts(p.data);
      setApproved(a.data);
      try {
        const m = await api.get(`/media`);
        const map = {};
        m.data.forEach((x) => (map[x.id] = x));
        setMedia(map);
      } catch (_) { /* members can't read /media; thumbnails come via post.attached_media_url */ }
    } catch (e) {
      toast.error("Failed to load approval queue");
    } finally {
      setLoading(false);
    }
    try {
      const n = await api.get(`/newsletters`);
      setNewsletters(n.data || []);
    } catch (_) { /* newsletters may be empty/unavailable for some roles — non-fatal */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); loadSocial(); loadBrand(); }, [load, loadSocial, loadBrand]);

  const approve = async (post) => {
    setBusy(post.id);
    try {
      const edited = editing[post.id];
      const body = { feedback: feedback[post.id] || null };
      if (edited !== undefined && edited !== post.draft_text) body.final_text = edited;
      const r = await api.post(`/posts/${post.id}/approve`, body);
      toast.success("Post approved");
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
      setApproved((prev) => [r.data, ...prev]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusy(null); }
  };

  const decline = async (postId) => {
    setBusy(postId);
    try {
      await api.post(`/posts/${postId}/decline`, { feedback: feedback[postId] || null });
      toast.success("Sent back for revision");
      setPosts((prev) => prev.filter((p) => p.id !== postId));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusy(null); }
  };

  const publishNow = async (post) => {
    setBusy(post.id);
    try {
      await api.post(`/posts/${post.id}/publish`);
      toast.success("Published to your connected accounts");
      setApproved((prev) => prev.map((p) => (p.id === post.id ? { ...p, status: "published", publish_status: "published" } : p)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    } finally { setBusy(null); }
  };

  const confirmDeletePost = async () => {
    const postId = deleteTarget;
    setDeleteTarget(null);
    setBusy(postId);
    try {
      await api.delete(`/posts/${postId}`);
      toast.success("Post deleted");
      setPosts((prev) => prev.filter((p) => p.id !== postId));
      setApproved((prev) => prev.filter((p) => p.id !== postId));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusy(null); }
  };

  const filteredReviewPosts = () => [...posts].filter((p) =>
    (filterMode === "all" || (filterMode === "ai" && p.ai_generated) || (filterMode === "text" && !p.attached_media_id) || (filterMode === "images" && !!p.attached_media_id))
    && matchesSearch(searchQuery, [p.draft_text, p.platform, p.status, media[p.attached_media_id]?.display_name, media[p.attached_media_id]?.filename]));

  const approveAndPublishAll = async () => {
    setApproveAllTarget(false);
    const targets = filteredReviewPosts();
    if (targets.length === 0) return;
    setBatchBusy(true);
    let approvedCount = 0, publishedCount = 0, failedCount = 0;
    for (const post of targets) {
      try {
        const edited = editing[post.id];
        const body = { feedback: feedback[post.id] || null };
        if (edited !== undefined && edited !== post.draft_text) body.final_text = edited;
        const r = await api.post(`/posts/${post.id}/approve`, body);
        approvedCount += 1;
        setPosts((prev) => prev.filter((p) => p.id !== post.id));
        setApproved((prev) => [r.data, ...prev]);
        if (canManageContent && canPublish(r.data)) {
          try {
            await api.post(`/posts/${post.id}/publish`);
            publishedCount += 1;
            setApproved((prev) => prev.map((p) => (p.id === post.id ? { ...p, status: "published", publish_status: "published" } : p)));
          } catch (_) { failedCount += 1; }
        }
      } catch (_) { failedCount += 1; }
    }
    setBatchBusy(false);
    const parts = [`Approved ${approvedCount}`];
    if (publishedCount) parts.push(`published ${publishedCount}`);
    if (failedCount) parts.push(`${failedCount} failed`);
    toast[failedCount ? "warning" : "success"](parts.join(", "));
  };

  const fmt = (d) => new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });

  const approveNewsletter = async (n) => {
    setNewsletterBusy(n.id);
    try {
      const r = await api.post(`/newsletters/${n.id}/approve`, { feedback: newsletterFeedback[n.id] || null });
      toast.success("Newsletter approved");
      setNewsletters((prev) => prev.map((x) => (x.id === n.id ? r.data : x)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setNewsletterBusy(null); }
  };

  const declineNewsletter = async (n) => {
    setNewsletterBusy(n.id);
    try {
      const r = await api.post(`/newsletters/${n.id}/decline`, { feedback: newsletterFeedback[n.id] || null });
      toast.success("Newsletter declined");
      setNewsletters((prev) => prev.map((x) => (x.id === n.id ? r.data : x)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setNewsletterBusy(null); }
  };

  const confirmDeleteNewsletter = async () => {
    const id = deleteNewsletterTarget;
    setDeleteNewsletterTarget(null);
    setNewsletterBusy(id);
    try {
      await api.delete(`/newsletters/${id}`);
      toast.success("Newsletter deleted");
      setNewsletters((prev) => prev.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setNewsletterBusy(null); }
  };

  const copyForPabau = async (n) => {
    const lines = [`Subject: ${n.subject}`, "", n.body];
    if (n.links?.length > 0) {
      lines.push("", "Links:");
      n.links.forEach((l) => lines.push(`- ${l.label}: ${l.tracked_url || l.url}`));
    }
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      toast.success("Copied — paste into Pabau's Broadcast composer");
    } catch {
      toast.error("Couldn't copy — try selecting the text manually");
    }
  };

  const copyNewsletterHtml = async (n) => {
    try {
      await navigator.clipboard.writeText(n.rendered_html || "");
      toast.success("HTML copied — paste it anywhere");
    } catch {
      toast.error("Couldn't copy — try selecting the preview manually");
    }
  };

  const sendViaMailchimp = async (n) => {
    setSendMailchimpTarget(null);
    setNewsletterBusy(n.id);
    try {
      const r = await api.post(`/newsletters/${n.id}/send`);
      toast.success("Sent via Mailchimp");
      setNewsletters((prev) => prev.map((x) => (x.id === n.id ? r.data : x)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mailchimp couldn't send it — try Open in Mailchimp to finish manually");
      await load();
    } finally { setNewsletterBusy(null); }
  };

  const scheduleNewsletter = async (n) => {
    if (!n) return;
    const val = scheduleTime[n.id];
    if (!val) { toast.error("Pick a date & time first"); return; }
    setNewsletterBusy(n.id);
    try {
      const iso = new Date(val).toISOString();
      const r = await api.post(`/newsletters/${n.id}/schedule`, { schedule_time: iso });
      toast.success("Scheduled — Mailchimp will send it automatically, no further click needed");
      setNewsletters((prev) => prev.map((x) => (x.id === n.id ? r.data : x)));
      setScheduleTarget(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't schedule it");
    } finally { setNewsletterBusy(null); }
  };

  const unscheduleNewsletter = async (n) => {
    if (!n) return;
    setNewsletterBusy(n.id);
    try {
      const r = await api.post(`/newsletters/${n.id}/unschedule`);
      toast.success("Schedule canceled");
      setNewsletters((prev) => prev.map((x) => (x.id === n.id ? r.data : x)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't cancel the schedule");
    } finally { setNewsletterBusy(null); setUnscheduleTarget(null); }
  };

  const canPublish = (post) => {
    const platforms = post.platforms || (post.platform ? [post.platform] : []);
    const hasMedia = !!(post.attached_media_id || post.attached_media_url);
    return platforms.some((p) => {
      if (p === "facebook") return !!fbDirect?.connected;
      if (p === "instagram") return !!fbDirect?.instagram_linked && hasMedia;
      return !!native?.[NATIVE_PROVIDER_FOR_PLATFORM[p]]?.ready;
    });
  };
  const anyConnected = !!fbDirect?.connected || !!fbDirect?.instagram_linked || (native && Object.values(native).some((p) => p?.ready));

  const filteredApproved = () => [...approved].filter((p) =>
    matchesSearch(searchQuery, [p.final_text || p.draft_text, p.platform, p.status, media[p.attached_media_id]?.display_name, media[p.attached_media_id]?.filename]));
  const filteredNewsletters = () => [...newsletters].filter((n) =>
    matchesSearch(searchQuery, [n.subject, n.body, n.status, n.campaign]));

  const downloadBlob = async (urlPath, fallbackName) => {
    try {
      const r = await api.get(urlPath, { responseType: "blob" });
      const cd = r.headers?.["content-disposition"] || "";
      const match = cd.match(/filename="([^"]+)"/);
      const contentType = r.headers?.["content-type"];
      const blobUrl = window.URL.createObjectURL(new Blob([r.data], contentType ? { type: contentType } : undefined));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = (match && match[1]) || fallbackName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    }
  };
  const downloadPostMedia = (post) => {
    const m = media[post.attached_media_id];
    if (!m) { toast.error("No attached media to download"); return; }
    // display_name (e.g. "Teshea Pitt - Move Better - V3") never has an extension — take the real
    // one from the stored filename, or Content-Disposition will override this anyway if exposed.
    const origExt = (m.filename || "").match(/\.([a-zA-Z0-9]{2,5})$/)?.[1];
    const ext = origExt || (m.kind === "video" ? "mp4" : m.kind === "audio" ? "mp3" : "png");
    const base = (m.display_name || m.filename || "download").replace(/\.[a-zA-Z0-9]{2,5}$/, "");
    downloadBlob(`/media/${m.id}/download`, `${base}.${ext}`);
  };
  const downloadNewsletter = (n, format) => downloadBlob(`/newsletters/${n.id}/export.${format}`, `${n.subject || "newsletter"}.${format}`);

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="text-[#3C4048] text-xl">Loading approvals…</div></div>;
  }

  return (
    <div className="space-y-8 max-w-6xl" data-testid="approval-queue">
      <div>
        <h1 className="text-4xl font-bold text-[#3C4048] mb-2 tracking-tight" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>Approval Queue</h1>
        <p className="text-[#7A9090]">Review posts, then approve and publish.</p>
      </div>

      {/* View tabs */}
      <div className="flex items-center gap-2" data-testid="approval-views">
        {[
          { k: "review", label: `Needs review${posts.length ? ` (${posts.length})` : ""}` },
          { k: "ready", label: `Ready to publish${approved.length ? ` (${approved.length})` : ""}` },
          { k: "newsletters", label: `Newsletters${newsletters.filter((n) => n.status === "pending_review").length ? ` (${newsletters.filter((n) => n.status === "pending_review").length})` : ""}` },
        ].map((t) => (
          <button key={t.k} onClick={() => setView(t.k)} data-testid={`view-${t.k}`}
            className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${view === t.k ? "bg-[#3A7A8C] text-white border-[#3A7A8C]" : "bg-white text-[#3C4048] border-[#e5e7eb] hover:border-[#5FA8B4]"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Search across whichever tab is active */}
      <div className="relative max-w-md">
        <MagnifyingGlass size={20} weight="regular" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7A9090]" />
        <Input
          data-testid="approval-search-input"
          type="text"
          placeholder="Search by caption, subject, platform, or asset name…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-white border-[#e5e7eb] text-[#3C4048] placeholder:text-[#9AA6AC] focus:border-[#5FA8B4]"
        />
      </div>

      {/* Social connection hint */}
      {view === "ready" && canManageContent && !anyConnected && (        <div className="flex items-start gap-2 bg-[#fff8e6] border border-[#f0d98a] rounded-[12px] p-3 text-sm" data-testid="social-not-connected-hint">
          <WarningCircle size={18} weight="fill" className="text-[#a16207] shrink-0 mt-0.5" />
          <p className="text-[#8a6d1a]">Connect your social accounts in <Link to="/settings" className="font-semibold underline">Settings</Link> to enable publishing and auto-scheduling.</p>
        </div>
      )}

      {/* NEEDS REVIEW */}
      {view === "review" && (
        posts.length === 0 ? (
          <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-12 text-center" data-testid="approval-empty">
            <CheckCircle size={40} weight="duotone" className="text-[#22c55e] mx-auto mb-3" />
            <p className="text-[#7A9090]">All caught up. Nothing awaiting approval.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 flex-wrap justify-between">
              <div className="flex items-center gap-2 flex-wrap" data-testid="approval-filters">
                {[{ k: "all", label: "All" }, { k: "ai", label: "AI only" }, { k: "text", label: "Text" }, { k: "images", label: "Images" }].map((f) => (
                  <button key={f.k} onClick={() => setFilterMode(f.k)} data-testid={`filter-${f.k}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${filterMode === f.k ? "bg-[#5FA8B4] text-white border-[#5FA8B4]" : "bg-white text-[#3C4048] border-[#e5e7eb] hover:border-[#5FA8B4]"}`}>
                    {f.label}
                  </button>
                ))}
              </div>
              {canApprove && filteredReviewPosts().length > 1 && (
                <Button data-testid="approve-publish-all-btn" disabled={batchBusy} onClick={() => setApproveAllTarget(true)}
                  className="bg-[#3A7A8C] text-white hover:bg-[#2c5f6e] rounded-[12px] gap-2 disabled:opacity-60">
                  <PaperPlaneTilt size={16} weight="fill" /> {batchBusy ? "Processing…" : `Approve & Publish All (${filteredReviewPosts().length})`}
                </Button>
              )}
            </div>
            <div className="space-y-5">
              {filteredReviewPosts().length === 0 && (
                <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-8 text-center text-[#7A9090]" data-testid="approval-search-no-results">
                  No posts match your search.
                </div>
              )}
              {filteredReviewPosts()
                .sort((a, b) => (b.ai_generated ? 1 : 0) - (a.ai_generated ? 1 : 0)).map((post) => {
                const isEditing = editing[post.id] !== undefined;
                return (
                  <div key={post.id} data-testid={`approval-card-${post.id}`} className="bg-white border border-[#e5e7eb] rounded-[12px] p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="px-2 py-1 rounded text-white text-xs font-semibold" style={{ backgroundColor: platformColors[post.platform] || "#7A9090" }}>{post.platform}</span>
                      {post.ai_generated && (
                        <span data-testid={`ai-badge-${post.id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#3A7A8C] text-white text-[10px] font-semibold">
                          <Sparkle size={11} weight="fill" /> AI · Needs review
                        </span>
                      )}
                      <span className="flex items-center gap-1 text-[#a16207] text-xs font-medium"><Clock size={12} /> {fmt(post.scheduled_date)}</span>
                      {hasUnread(post) && <span data-testid={`unread-${post.id}`} className="w-2 h-2 rounded-full bg-[#ef4444]" title="New comments" />}
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                      <div className="lg:col-span-6 flex flex-col items-center gap-2">
                        <div className="w-full flex justify-center bg-[#ECEEEA] rounded-[16px] p-4">
                          <PlatformMockup platform={post.platform} mediaUrl={post.attached_media_url} mediaKind={post.attached_media_kind}
                            caption={isEditing ? editing[post.id] : post.draft_text} brandName={brand.name} brandLogoUrl={brand.logoUrl} />
                        </div>
                        <Button data-testid={`full-preview-btn-${post.id}`} onClick={() => openPostFullPreview(post, isEditing ? editing[post.id] : post.draft_text)}
                          size="sm" variant="outline" className="border-[#5FA8B4] text-[#3A7A8C] hover:bg-[#f0f7f8] rounded-[10px] gap-1.5 h-8">
                          <Eye size={14} /> Full Preview
                        </Button>
                        {post.attached_media_id && (
                          <Button data-testid={`download-media-btn-${post.id}`} onClick={() => downloadPostMedia(post)}
                            size="sm" variant="outline" className="border-[#5FA8B4] text-[#3A7A8C] hover:bg-[#f0f7f8] rounded-[10px] gap-1.5 h-8">
                            <DownloadSimple size={14} /> Download
                          </Button>
                        )}
                        {canManageContent && post.attached_media_url && media[post.attached_media_id] && post.attached_media_kind !== "video" && (
                          <Button data-testid={`edit-image-btn-${post.id}`} onClick={() => setEditMedia({ postId: post.id, media: media[post.attached_media_id] })}
                            size="sm" variant="outline" className="border-[#5FA8B4] text-[#3A7A8C] hover:bg-[#f0f7f8] rounded-[10px] gap-1.5 h-8">
                            <PencilSimple size={14} /> Edit image
                          </Button>
                        )}
                      </div>
                      <div className="lg:col-span-6 flex flex-col gap-3 min-w-0">
                        <button onClick={() => copyPlain(post.draft_text)} className="self-start flex items-center gap-1.5 text-[#7A9090] hover:text-[#3A7A8C] transition-colors text-xs font-medium" data-testid={`copy-draft-${post.id}`}>
                          <Copy size={13} /> Copy caption as plain text
                        </button>
                        {isEditing && (
                          <Textarea data-testid={`approval-edit-${post.id}`} value={editing[post.id]}
                            onChange={(e) => setEditing((s) => ({ ...s, [post.id]: e.target.value }))}
                            className="bg-[#fafafa] border-[#e5e7eb] text-[#3C4048] min-h-[120px] whitespace-pre-line" />
                        )}
                        <button data-testid={`comments-toggle-${post.id}`} onClick={() => setOpenComments((s) => ({ ...s, [post.id]: !s[post.id] }))}
                          className="flex items-center gap-1.5 text-[#3A7A8C] text-sm font-medium">
                          <ChatCircleDots size={16} /> {openComments[post.id] ? "Hide discussion" : "Discussion"}
                          {post.comment_count > 0 && <span className="text-[#7A9090] text-xs">({post.comment_count})</span>}
                        </button>
                        {openComments[post.id] && <div><CommentsThread api={api} postId={post.id} currentName={user?.fullName || user?.firstName || "Staff"} /></div>}

                        {canApprove && (
                          <div className="space-y-3">
                            <Textarea data-testid={`approval-feedback-${post.id}`} placeholder="Optional feedback for the team…"
                              value={feedback[post.id] || ""} onChange={(e) => setFeedback((f) => ({ ...f, [post.id]: e.target.value }))}
                              className="bg-[#fafafa] border-[#e5e7eb] text-[#3C4048] min-h-[60px]" />
                            <div className="flex flex-wrap gap-3">
                              {canManageContent && (
                                <Button data-testid={`edit-btn-${post.id}`} onClick={() => setEditing((s) => isEditing ? (({ [post.id]: _, ...rest }) => rest)(s) : ({ ...s, [post.id]: post.draft_text }))}
                                  variant="outline" className="border-[#5FA8B4] text-[#3A7A8C] hover:bg-[#f0f7f8] rounded-[12px] gap-2">
                                  <PencilSimple size={16} /> {isEditing ? "Cancel edit" : "Edit"}
                                </Button>
                              )}
                              <Button data-testid={`approve-btn-${post.id}`} disabled={busy === post.id} onClick={() => approve(post)} className="flex-1 min-w-[120px] bg-[#22c55e] text-white hover:bg-[#16a34a] rounded-[12px]">
                                <CheckCircle size={18} className="mr-2" weight="bold" /> {isEditing ? "Save & Approve" : "Approve"}
                              </Button>
                              <Button data-testid={`decline-btn-${post.id}`} disabled={busy === post.id} onClick={() => decline(post.id)} className="flex-1 min-w-[120px] bg-white border border-[#ef4444] text-[#ef4444] hover:bg-[#ef4444] hover:text-white rounded-[12px]">
                                <XCircle size={18} className="mr-2" weight="bold" /> Decline
                              </Button>
                            </div>
                          </div>
                        )}
                        {canDelete && (
                          <button data-testid={`delete-post-btn-${post.id}`} disabled={busy === post.id} onClick={() => setDeleteTarget(post.id)}
                            className="self-start flex items-center gap-1.5 text-[#7A9090] hover:text-[#ef4444] transition-colors text-xs font-medium">
                            <Trash size={13} /> Delete post
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )
      )}

      {/* READY TO PUBLISH */}
      {view === "ready" && (
        approved.length === 0 ? (
          <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-12 text-center" data-testid="ready-empty">
            <PaperPlaneTilt size={38} weight="duotone" className="text-[#5FA8B4] mx-auto mb-3" />
            <p className="text-[#7A9090]">No approved posts yet. Approve posts to line them up for publishing.</p>
          </div>
        ) : (
          <div className="space-y-5">
            {filteredApproved().length === 0 && (
              <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-8 text-center text-[#7A9090]" data-testid="approval-ready-search-no-results">
                No approved posts match your search.
              </div>
            )}
            {filteredApproved().map((post) => {
              const published = post.status === "published" || post.publish_status === "published";
              const failed = post.publish_status === "failed";
              return (
                <div key={post.id} data-testid={`ready-card-${post.id}`} className="bg-white border border-[#e5e7eb] rounded-[12px] p-5">
                  <div className="flex items-center gap-2 mb-4 flex-wrap">
                    <span className="px-2 py-1 rounded text-white text-xs font-semibold" style={{ backgroundColor: platformColors[post.platform] || "#7A9090" }}>{post.platform}</span>
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#dcfce7] text-[#15803d] text-[10px] font-semibold"><CheckCircle size={11} weight="fill" /> Approved</span>
                    {published && <span data-testid={`published-badge-${post.id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#5FA8B4] text-white text-[10px] font-semibold"><PaperPlaneTilt size={11} weight="fill" /> Published</span>}
                    {failed && <span data-testid={`failed-badge-${post.id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#fee2e2] text-[#b91c1c] text-[10px] font-semibold"><WarningCircle size={11} weight="fill" /> Publish failed</span>}
                    <span className="flex items-center gap-1 text-[#7A9090] text-xs font-medium"><Clock size={12} /> {fmt(post.scheduled_date)}</span>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    <div className="lg:col-span-6 flex justify-center">
                      <div className="w-full flex flex-col items-center gap-2">
                        <div className="w-full flex justify-center bg-[#ECEEEA] rounded-[16px] p-4">
                          <PlatformMockup platform={post.platform} mediaUrl={post.attached_media_url} mediaKind={post.attached_media_kind}
                            caption={post.final_text || post.draft_text} brandName={brand.name} brandLogoUrl={brand.logoUrl} />
                        </div>
                        <Button data-testid={`full-preview-btn-${post.id}`} onClick={() => openPostFullPreview(post, post.final_text || post.draft_text)}
                          size="sm" variant="outline" className="border-[#5FA8B4] text-[#3A7A8C] hover:bg-[#f0f7f8] rounded-[10px] gap-1.5 h-8">
                          <Eye size={14} /> Full Preview
                        </Button>
                        {post.attached_media_id && (
                          <Button data-testid={`download-media-btn-${post.id}`} onClick={() => downloadPostMedia(post)}
                            size="sm" variant="outline" className="border-[#5FA8B4] text-[#3A7A8C] hover:bg-[#f0f7f8] rounded-[10px] gap-1.5 h-8">
                            <DownloadSimple size={14} /> Download
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="lg:col-span-6 flex flex-col gap-3 min-w-0">
                      <button onClick={() => copyPlain(post.final_text || post.draft_text)} className="self-start flex items-center gap-1.5 text-[#7A9090] hover:text-[#3A7A8C] transition-colors text-xs font-medium" data-testid={`copy-final-${post.id}`}>
                        <Copy size={13} /> Copy caption as plain text
                      </button>
                      {canManageContent && !published && post.attached_media_url && media[post.attached_media_id] && post.attached_media_kind !== "video" && (
                        <Button data-testid={`edit-ready-image-btn-${post.id}`} onClick={() => setEditMedia({ postId: post.id, media: media[post.attached_media_id] })}
                          size="sm" variant="outline" className="border-[#5FA8B4] text-[#3A7A8C] hover:bg-[#f0f7f8] rounded-[10px] gap-1.5 h-8 self-start">
                          <PencilSimple size={14} /> Edit image
                        </Button>
                      )}
                      {failed && post.publish_error && <p className="text-[#b91c1c] text-xs">{post.publish_error}</p>}
                      {canManageContent && !published && (
                        <Button data-testid={`publish-now-btn-${post.id}`} disabled={busy === post.id || !canPublish(post)} onClick={() => publishNow(post)}
                          className="bg-[#5FA8B4] text-white hover:bg-[#3A7A8C] rounded-[12px] gap-2 disabled:opacity-50 self-start">
                          <PaperPlaneTilt size={18} weight="fill" /> {busy === post.id ? "Publishing…" : failed ? "Retry publish" : "Publish now"}
                        </Button>
                      )}
                      {canDelete && (
                        <button data-testid={`delete-post-btn-${post.id}`} disabled={busy === post.id} onClick={() => setDeleteTarget(post.id)}
                          className="self-start flex items-center gap-1.5 text-[#7A9090] hover:text-[#ef4444] transition-colors text-xs font-medium">
                          <Trash size={13} /> Delete post
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* NEWSLETTERS */}
      {view === "newsletters" && (
        newsletters.length === 0 ? (
          <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-12 text-center" data-testid="newsletters-empty">
            <EnvelopeSimple size={38} weight="duotone" className="text-[#5FA8B4] mx-auto mb-3" />
            <p className="text-[#7A9090]">No newsletter drafts yet. Ask Shee in Creative Director to draft one.</p>
          </div>
        ) : (
          <div className="space-y-5">
            {filteredNewsletters().length === 0 && (
              <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-8 text-center text-[#7A9090]" data-testid="approval-newsletters-search-no-results">
                No newsletters match your search.
              </div>
            )}
            {filteredNewsletters().map((n) => {
              const isPending = n.status === "pending_review";
              const isApproved = n.status === "approved";
              const isDeclined = n.status === "declined";
              return (
                <div key={n.id} data-testid={`newsletter-card-${n.id}`} className="bg-white border border-[#e5e7eb] rounded-[12px] p-5">
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-white text-xs font-semibold bg-[#3A7A8C]">
                      <EnvelopeSimple size={12} weight="fill" /> Newsletter
                    </span>
                    {isPending && <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#fff8e6] text-[#a16207] text-[10px] font-semibold"><Clock size={11} weight="fill" /> Needs review</span>}
                    {isApproved && <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#dcfce7] text-[#15803d] text-[10px] font-semibold"><CheckCircle size={11} weight="fill" /> Approved</span>}
                    {isDeclined && <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#fee2e2] text-[#b91c1c] text-[10px] font-semibold"><XCircle size={11} weight="fill" /> Declined</span>}
                  </div>
                  <p className="text-[#3C4048] font-semibold mb-1" data-testid={`newsletter-subject-${n.id}`}>{n.subject}</p>
                  {(n.mailchimp_list_name || n.from_name || n.preview_text) && (
                    <div className="text-[#7A9090] text-xs mb-2 space-y-0.5" data-testid={`newsletter-mc-meta-${n.id}`}>
                      {n.mailchimp_list_name && <p>Audience: <span className="text-[#3C4048] font-medium">{n.mailchimp_list_name}</span></p>}
                      {n.from_name && <p>From: <span className="text-[#3C4048] font-medium">{n.from_name}{n.from_email ? ` <${n.from_email}>` : ""}</span></p>}
                      {n.preview_text && <p>Preview text: <span className="text-[#3C4048] font-medium">{n.preview_text}</span></p>}
                    </div>
                  )}
                  <p className="text-[#3C4048] text-sm whitespace-pre-line mb-3" data-testid={`newsletter-body-${n.id}`}>{n.body}</p>
                  {n.rendered_html && (
                    <div className="mb-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[#7A9090] text-xs font-semibold uppercase tracking-wide">Email preview</span>
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            data-testid={`full-preview-newsletter-btn-${n.id}`}
                            onClick={() => openNewsletterFullPreview(n)}
                            className="inline-flex items-center gap-1 text-[#3A7A8C] text-xs font-semibold hover:underline"
                          >
                            <Eye size={12} /> Full Preview
                          </button>
                          <button
                            type="button"
                            data-testid={`open-full-preview-btn-${n.id}`}
                            onClick={() => {
                              const blob = new Blob([n.rendered_html], { type: "text/html" });
                              const url = URL.createObjectURL(blob);
                              window.open(url, "_blank");
                            }}
                            className="inline-flex items-center gap-1 text-[#3A7A8C] text-xs font-semibold hover:underline"
                          >
                            <ArrowSquareOut size={12} /> Open full preview
                          </button>
                          <button
                            type="button"
                            data-testid={`copy-html-btn-${n.id}`}
                            onClick={() => copyNewsletterHtml(n)}
                            className="inline-flex items-center gap-1 text-[#3A7A8C] text-xs font-semibold hover:underline"
                          >
                            <Copy size={12} /> Copy HTML
                          </button>
                          <button
                            type="button"
                            data-testid={`download-pdf-btn-${n.id}`}
                            onClick={() => downloadNewsletter(n, "pdf")}
                            className="inline-flex items-center gap-1 text-[#3A7A8C] text-xs font-semibold hover:underline"
                          >
                            <FilePdf size={12} /> Download PDF
                          </button>
                          <button
                            type="button"
                            data-testid={`download-png-btn-${n.id}`}
                            onClick={() => downloadNewsletter(n, "png")}
                            className="inline-flex items-center gap-1 text-[#3A7A8C] text-xs font-semibold hover:underline"
                          >
                            <FileImage size={12} /> Download PNG
                          </button>
                          <button
                            type="button"
                            data-testid={`download-html-btn-${n.id}`}
                            onClick={() => downloadNewsletter(n, "html")}
                            className="inline-flex items-center gap-1 text-[#3A7A8C] text-xs font-semibold hover:underline"
                          >
                            <FileArrowDown size={12} /> Download HTML
                          </button>
                        </div>
                      </div>
                      <iframe
                        data-testid={`newsletter-html-preview-${n.id}`}
                        title={`Newsletter HTML preview — ${n.subject}`}
                        srcDoc={n.rendered_html}
                        sandbox=""
                        className="w-full h-[650px] rounded-[10px] border border-[#e5e7eb] bg-white"
                      />
                    </div>
                  )}
                  {n.links?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3" data-testid={`newsletter-links-${n.id}`}>
                      {n.links.map((l, i) => (
                        <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#f0f7f8] text-[#3A7A8C] text-xs" title={l.tracked_url || l.url}>
                          <LinkIcon size={12} /> {l.label}
                        </span>
                      ))}
                    </div>
                  )}
                  {n.feedback && <p className="text-[#7A9090] text-xs mb-3">Feedback: {n.feedback}</p>}
                  {n.send_status === "sent" && (
                    <p className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#dcfce7] text-[#15803d] text-[10px] font-semibold mb-3" data-testid={`newsletter-sent-badge-${n.id}`}>
                      <CheckCircle size={11} weight="fill" /> Sent via Mailchimp{n.sent_at ? ` — ${new Date(n.sent_at).toLocaleString()}` : ""}
                    </p>
                  )}
                  {n.send_status === "scheduled" && (
                    <div className="flex items-center gap-2 flex-wrap mb-3">
                      <p className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#e0f2fe] text-[#0369a1] text-[10px] font-semibold" data-testid={`newsletter-scheduled-badge-${n.id}`}>
                        <Clock size={11} weight="fill" /> Scheduled — sends automatically {n.scheduled_send_time ? new Date(n.scheduled_send_time).toLocaleString() : ""}
                      </p>
                      {canApprove && (
                        <button data-testid={`cancel-schedule-btn-${n.id}`} disabled={newsletterBusy === n.id} onClick={() => setUnscheduleTarget(n)}
                          className="inline-flex items-center gap-1 text-[#b91c1c] text-xs font-semibold hover:underline">
                          <Prohibit size={12} /> Cancel schedule
                        </button>
                      )}
                    </div>
                  )}
                  {n.send_status === "send_failed" && (
                    <p className="flex items-center gap-1.5 text-[#b91c1c] text-xs mb-3" data-testid={`newsletter-send-error-${n.id}`}>
                      <WarningCircle size={13} weight="fill" /> Mailchimp couldn't send it — finish it manually below.
                    </p>
                  )}
                  {n.mailchimp_error && isApproved && !n.mailchimp_campaign_id && (
                    <p className="flex items-center gap-1.5 text-[#b91c1c] text-xs mb-3" data-testid={`newsletter-mailchimp-error-${n.id}`}>
                      <WarningCircle size={13} weight="fill" /> Mailchimp draft creation failed: {n.mailchimp_error}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-3 items-center">
                    {isPending && canApprove && (
                      <>
                        <Textarea data-testid={`newsletter-feedback-${n.id}`} placeholder="Optional feedback…"
                          value={newsletterFeedback[n.id] || ""} onChange={(e) => setNewsletterFeedback((f) => ({ ...f, [n.id]: e.target.value }))}
                          className="bg-[#fafafa] border-[#e5e7eb] text-[#3C4048] min-h-[44px] w-full" />
                        <Button data-testid={`approve-newsletter-btn-${n.id}`} disabled={newsletterBusy === n.id} onClick={() => approveNewsletter(n)} className="flex-1 min-w-[120px] bg-[#22c55e] text-white hover:bg-[#16a34a] rounded-[12px]">
                          <CheckCircle size={18} className="mr-2" weight="bold" /> Approve
                        </Button>
                        <Button data-testid={`decline-newsletter-btn-${n.id}`} disabled={newsletterBusy === n.id} onClick={() => declineNewsletter(n)} className="flex-1 min-w-[120px] bg-white border border-[#ef4444] text-[#ef4444] hover:bg-[#ef4444] hover:text-white rounded-[12px]">
                          <XCircle size={18} className="mr-2" weight="bold" /> Decline
                        </Button>
                      </>
                    )}
                    {isApproved && n.mailchimp_campaign_id && n.send_status !== "sent" && n.send_status !== "scheduled" && canApprove && (
                      <>
                        <Button data-testid={`send-mailchimp-btn-${n.id}`} disabled={newsletterBusy === n.id} onClick={() => setSendMailchimpTarget(n)} className="bg-[#22c55e] text-white hover:bg-[#16a34a] rounded-[12px] gap-2">
                          <PaperPlaneTilt size={16} /> {newsletterBusy === n.id ? "Sending…" : "Send via Mailchimp"}
                        </Button>
                        <Button data-testid={`schedule-newsletter-btn-${n.id}`} disabled={newsletterBusy === n.id} onClick={() => setScheduleTarget(n)} className="bg-white border border-[#3A7A8C] text-[#3A7A8C] hover:bg-[#f0f7f8] rounded-[12px] gap-2">
                          <CalendarPlus size={16} /> Schedule for later
                        </Button>
                      </>
                    )}
                    {n.mailchimp_edit_url && (
                      <a data-testid={`open-mailchimp-link-${n.id}`} href={n.mailchimp_edit_url} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1.5 text-[#3A7A8C] text-xs font-semibold hover:underline">
                        <ArrowSquareOut size={14} weight="bold" /> Open in Mailchimp
                      </a>
                    )}
                    {isApproved && !n.mailchimp_campaign_id && n.mailchimp_error && canApprove && (
                      <Button data-testid={`retry-mailchimp-btn-${n.id}`} disabled={newsletterBusy === n.id} onClick={() => approveNewsletter(n)} className="bg-[#3A7A8C] text-white hover:bg-[#2d6373] rounded-[12px] gap-2">
                        <PaperPlaneTilt size={16} /> {newsletterBusy === n.id ? "Retrying…" : "Retry Mailchimp"}
                      </Button>
                    )}
                    {isApproved && !n.mailchimp_campaign_id && (
                      <Button data-testid={`copy-for-pabau-btn-${n.id}`} onClick={() => copyForPabau(n)} className="bg-[#5FA8B4] text-white hover:bg-[#3A7A8C] rounded-[12px] gap-2">
                        <Copy size={16} /> Copy for Pabau
                      </Button>
                    )}
                    {canDelete && (
                      <button data-testid={`delete-newsletter-btn-${n.id}`} disabled={newsletterBusy === n.id} onClick={() => setDeleteNewsletterTarget(n.id)}
                        className="flex items-center gap-1.5 text-[#7A9090] hover:text-[#ef4444] transition-colors text-xs font-medium">
                        <Trash size={13} /> Delete
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {editMedia && (
        <ImageEditor
          open={!!editMedia}
          onOpenChange={(v) => !v && setEditMedia(null)}
          src={editMedia.media.file_url}
          filename={editMedia.media.filename}
          onSaved={(m) => { setMedia((prev) => ({ ...prev, [m.id]: m })); }}
          swap={{
            label: "Use in this post",
            handler: async (m) => {
              try {
                await api.put(`/posts/${editMedia.postId}`, { attached_media_id: m.id });
                setPosts((prev) => prev.map((p) => (p.id === editMedia.postId ? { ...p, attached_media_id: m.id, attached_media_url: m.file_url, attached_media_kind: m.kind } : p)));
                setApproved((prev) => prev.map((p) => (p.id === editMedia.postId ? { ...p, attached_media_id: m.id, attached_media_url: m.file_url, attached_media_kind: m.kind } : p)));
                setMedia((prev) => ({ ...prev, [m.id]: m }));
                toast.success("Post image updated");
              } catch (e) { toast.error("Failed to update post image"); }
              setEditMedia(null);
            },
          }}
        />
      )}

      <FullPreviewModal open={!!fullPreview} onOpenChange={(v) => !v && setFullPreview(null)} preview={fullPreview} />

      <AlertDialog open={approveAllTarget} onOpenChange={(v) => !v && setApproveAllTarget(false)}>
        <AlertDialogContent className="bg-white border-[#e5e7eb]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#3C4048]" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>Approve & publish {filteredReviewPosts().length} post(s)?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#7A9090]">
              This approves every post currently shown (respecting your filter) and immediately tries to
              publish each to its connected platform. Posts with no connected platform are approved and
              left in "Ready to publish" for manual publishing. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="approve-publish-all-cancel" className="border-[#e5e7eb] text-[#3C4048]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="approve-publish-all-confirm" onClick={approveAndPublishAll} className="bg-[#3A7A8C] text-white hover:bg-[#2c5f6e]">
              Approve & Publish
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-white border-[#e5e7eb]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#3C4048]" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>Delete this post?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#7A9090]">This permanently removes the post, including if it's already approved or published. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-post-cancel" className="border-[#e5e7eb] text-[#3C4048]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-post-confirm" disabled={!!busy} onClick={confirmDeletePost} className="bg-[#ef4444] text-white hover:bg-[#dc2626] disabled:opacity-60">
              {busy ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteNewsletterTarget} onOpenChange={(v) => !v && setDeleteNewsletterTarget(null)}>
        <AlertDialogContent className="bg-white border-[#e5e7eb]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#3C4048]" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>Delete this newsletter?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#7A9090]">This permanently removes the newsletter draft. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-newsletter-cancel" className="border-[#e5e7eb] text-[#3C4048]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-newsletter-confirm" disabled={!!newsletterBusy} onClick={confirmDeleteNewsletter} className="bg-[#ef4444] text-white hover:bg-[#dc2626] disabled:opacity-60">
              {newsletterBusy ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!sendMailchimpTarget} onOpenChange={(v) => !v && setSendMailchimpTarget(null)}>
        <AlertDialogContent className="bg-white border-[#e5e7eb]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#3C4048]" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>Send via Mailchimp?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#7A9090]">Send "{sendMailchimpTarget?.subject}" now via Mailchimp? This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="send-mailchimp-cancel" className="border-[#e5e7eb] text-[#3C4048]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="send-mailchimp-confirm" disabled={!!newsletterBusy} onClick={() => sendViaMailchimp(sendMailchimpTarget)} className="bg-[#22c55e] text-white hover:bg-[#16a34a] disabled:opacity-60">
              Send
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!scheduleTarget} onOpenChange={(v) => !v && setScheduleTarget(null)}>
        <DialogContent className="bg-white border-[#e5e7eb]">
          <DialogHeader>
            <DialogTitle className="text-[#3C4048]" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>Schedule "{scheduleTarget?.subject}"</DialogTitle>
          </DialogHeader>
          <p className="text-[#7A9090] text-sm">
            Mailchimp will send this automatically at the time you pick — no further click needed. Must be
            at least 10 minutes from now; times round up to the nearest 15 minutes.
          </p>
          <input
            data-testid="newsletter-schedule-datetime"
            type="datetime-local"
            min={new Date(Date.now() + 10 * 60000 - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)}
            value={scheduleTime[scheduleTarget?.id] || ""}
            onChange={(e) => setScheduleTime((s) => ({ ...s, [scheduleTarget.id]: e.target.value }))}
            className="w-full bg-[#fafafa] border border-[#e5e7eb] text-[#3C4048] rounded-[10px] p-2 text-sm"
          />
          <DialogFooter>
            <Button data-testid="newsletter-schedule-cancel" onClick={() => setScheduleTarget(null)} className="bg-white border border-[#e5e7eb] text-[#3C4048] hover:bg-[#f5f7f7] rounded-[12px]">
              Cancel
            </Button>
            <Button data-testid="newsletter-schedule-confirm" disabled={newsletterBusy === scheduleTarget?.id} onClick={() => scheduleNewsletter(scheduleTarget)} className="bg-[#22c55e] text-white hover:bg-[#16a34a] rounded-[12px] disabled:opacity-60">
              {newsletterBusy === scheduleTarget?.id ? "Scheduling…" : "Schedule send"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!unscheduleTarget} onOpenChange={(v) => !v && setUnscheduleTarget(null)}>
        <AlertDialogContent className="bg-white border-[#e5e7eb]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#3C4048]" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>Cancel this scheduled send?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#7A9090]">"{unscheduleTarget?.subject}" will no longer send automatically. You can reschedule or send it manually later.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="unschedule-newsletter-cancel" className="border-[#e5e7eb] text-[#3C4048]">Keep scheduled</AlertDialogCancel>
            <AlertDialogAction data-testid="unschedule-newsletter-confirm" disabled={!!newsletterBusy} onClick={() => unscheduleNewsletter(unscheduleTarget)} className="bg-[#ef4444] text-white hover:bg-[#dc2626] disabled:opacity-60">
              {newsletterBusy ? "Canceling…" : "Cancel schedule"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
